/**
 * API utilities for Garagr Vue applications
 * Handles CSRF tokens, fetch operations, and error handling
 *
 * CRITICAL CONFIGURATION:
 * - MUST send token in X-XSRF-TOKEN header (ASP.NET Core standard)
 * - MUST include credentials: 'same-origin' to send antiforgery cookie
 * - DO NOT modify header name or credentials setting without consulting docs
 *
 * See: docs/architecture/vuejs-rest-api.md#antiforgery-token-requirements
 */

/**
 * Get CSRF token from the page
 * Checks multiple possible sources for the antiforgery token
 * @returns {string} CSRF token or empty string if not found
 */
export function getCsrfToken() {
  // Try standard ASP.NET Core antiforgery token input
  let tokenInput = document.querySelector('input[name="__RequestVerificationToken"]');

  // If not found, try looking specifically in forms
  if (!tokenInput) {
    tokenInput = document.querySelector('form input[name="__RequestVerificationToken"]');
  }

  // If not found, try the antiForgeryForm specifically
  if (!tokenInput) {
    tokenInput = document.querySelector('#antiForgeryForm input[name="__RequestVerificationToken"]');
  }

  // If not found, try the toggleForm (legacy pattern)
  if (!tokenInput) {
    tokenInput = document.querySelector('#toggleForm input[name="__RequestVerificationToken"]');
  }

  const token = tokenInput?.value || '';

  // Log warning if token not found (helps with debugging)
  if (!token) {
    console.warn('CSRF token not found. API requests may fail. Ensure the page includes @Html.AntiForgeryToken() or the _AntiForgeryTokenProvider partial.');
  }

  return token;
}

/**
 * Make an API request with standardized error handling
 * @param {string} url - API endpoint URL
 * @param {Object} options - Fetch options
 * @returns {Promise<any>} JSON response
 * @throws {Error} If request fails
 * @example
 * const data = await apiRequest('/api/listings', { method: 'GET' })
 */
export async function apiRequest(url, options = {}) {
  console.log(`=== API REQUEST: ${options.method || 'GET'} ${url} ===`);
  console.log('Request options:', options);
  
  const defaultHeaders = {
    'Content-Type': 'application/json',
    'X-XSRF-TOKEN': getCsrfToken()
  };

  const config = {
    credentials: 'same-origin',
    ...options,
    headers: {
      ...defaultHeaders,
      ...options.headers
    }
  };

  // Don't set Content-Type for FormData - browser will set it with boundary
  if (options.body instanceof FormData) {
    delete config.headers['Content-Type'];
  }

  console.log('Fetch config:', { url, ...config, body: config.body ? '(see below)' : undefined });
  if (config.body && typeof config.body === 'string') {
    console.log('Request body:', config.body.substring(0, 500) + (config.body.length > 500 ? '... (truncated)' : ''));
  }

  const response = await fetch(url, config);
  
  console.log(`Response status: ${response.status} ${response.statusText}`);
  console.log('Response headers:', Object.fromEntries(response.headers.entries()));

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'An error occurred' }));
    console.error('API ERROR:', error);
    throw new Error(error.error || error.message || `HTTP ${response.status}`);
  }

  // Handle empty responses
  const text = await response.text();
  const result = text ? JSON.parse(text) : null;
  console.log('Response data:', result);
  console.log(`=== API REQUEST END ===`);
  return result;
}

/**
 * Make a GET request
 * @param {string} url - API endpoint URL
 * @returns {Promise<any>} JSON response
 * @example
 * const listings = await apiGet('/api/listings')
 */
export async function apiGet(url) {
  return apiRequest(url, { method: 'GET' });
}

/**
 * Make a POST request with JSON body
 * @param {string} url - API endpoint URL
 * @param {Object} data - Data to send in request body
 * @returns {Promise<any>} JSON response
 * @example
 * const result = await apiPost('/api/listings', { title: 'New Listing' })
 */
export async function apiPost(url, data) {
  return apiRequest(url, {
    method: 'POST',
    body: JSON.stringify(data)
  });
}

/**
 * Make a DELETE request
 * @param {string} url - API endpoint URL
 * @returns {Promise<any>} JSON response
 * @example
 * await apiDelete('/api/listings/123')
 */
export async function apiDelete(url) {
  return apiRequest(url, { method: 'DELETE' });
}

/**
 * Upload a file using FormData
 * @param {string} url - API endpoint URL
 * @param {FormData} formData - FormData with file
 * @returns {Promise<any>} JSON response
 * @example
 * const formData = new FormData()
 * formData.append('file', fileBlob)
 * await apiUpload('/upload/image/123', formData)
 */
export async function apiUpload(url, formData) {
  return apiRequest(url, {
    method: 'POST',
    body: formData
  });
}
